import React from 'react';
import styled from '@emotion/styled';
import { bool, shape, string, arrayOf, number, oneOfType, func, object, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { uniqueId } from 'lodash';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableExpandHeader,
  TableRow,
  TableExpandRow,
  TableExpandedRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
  StructuredListWrapper,
  StructuredListBody,
  StructuredListRow,
  StructuredListCell,
  StructuredListSkeleton,
  Loading,
} from '@carbon/react';
import { Info, Pencil, Trash, Truck, MapPinCheckInside, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp, Eye } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import OrdersPagination from 'Containers/Orders/OrdersPagination';
import FilterOrders from 'Containers/Orders/FilterOrders';
import { CURRENCY_RON } from 'Constants';
import { AnalyticsLink, NWNotification } from 'Components/shared';

import IconWarranty from 'Assets/images/warranty.svg';
import IconInvoice from 'Assets/images/pdf.svg';
import IconCSV from 'Assets/images/csv.svg';

const statusColors = ['#999', '#1893d0', '#813d94', '#e8720b', '#01a982', '#68a765', '#d52b1e', '#a67c52', '#999', '#67ccff', '#67ccff'];

const StyledOrders = styled.div`
  .pageTitle {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 2px solid #E2E2E2;

    .pageTitleActions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
      align-items: center;

      button {
        cursor: pointer;
        background-color: transparent;
        border: none;
        padding: 0;

        svg {
          width: 24px;
          height: 30px;
        }
      }
    }
  }

  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead {
      background: #e0e0e0;
      font-weight: 600;

      th {
        color: #3a3a3a;
        font-size: 16px;
        font-weight: 600;
        background: #f5f7f9;

        .cds--table-header-label {
          font-size: 16px;
          font-weight: 500;

          button {
            width: 100%;
            cursor: pointer;
            display: flex;
            align-items: center;
            background: transparent;
            border: none;
            gap: 5px;
            font-size: inherit;
            font-weight: inherit;

            svg {
              width: 18px;
              height: 18px;
              stroke: #707070;
              min-width: 18px;
            }
          }
        }
      }
    }

    tbody {
      tr {

        &:hover.cds--expandable-row td {
          background: #040F2A;
        }

        &.reservation {
          background: #f3f2f2;

          &.cds--expandable-row td {
            background: #040F2A;
          }

          td {
            background: inherit;
          }
        }

        .orderStatus {
          display: flex;
          align-items: center;

          .expirationDate {
            color: #813d94;
            font-weight: inherit;
            padding-left: 10px;
          }
        }

        .supplierStock {
          color: #3749ee;
        }


        &:hover {
          background: #e5e5e5;

          td {
            background: inherit;
          }
        }

        td {
          background: #fff;

          .dropShipment {
            svg {
              width: 20px;
            }
          }

          .orderNumber {
             > span {
              display: inline-block;
            }
            .mobileTableHeader {
              display: none;
            }
          } 

          .totalValue {
            display: flex;
            align-items: center;
            gap: 5px;
            justify-content: flex-end;
          }

          .actions {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 5px;

            .downloads {
              display: flex;

              button > svg {
                width: 24px;
              }
            }

            a {
              padding: 7px;
              border: 1px solid #dde6f2;

              span {
                display: none;
              }

              &.viewOrder {
                background: #fef2e8;

                &:hover {
                  background: #fbb2ac;
                }
              }

              &.editOrder {
                background: #fff;
                
                &.disableLink {
                  pointer-events: none;
                }

                &:hover {
                  background: #d0ddef;
                }
              }

              svg {
                stroke: #3a3a3a;
              }
            }

            button {
              padding: 7px;
              border: 1px solid #dde6f2;
              cursor: pointer;

              &.deleteOrder {
                background: #fef2e8;

                &:hover {
                  background: #fbb2ac;
                }

                span {
                  display: none;
                }
              }

              &.download {
                padding: 7px 3px;
                border: none;
                background: transparent;
              }

              .cds--loading {
                left: 0;
              }
            }

            a, button {
              display: flex;
              align-items: center;
              justify-content: center;
            }
          }
        }
      }
    }
  }

  tr.cds--parent-row.cds--expandable-row {
    
    + tr[data-child-row] td .cds--child-row-inner-container {
      max-height: 256px;
      overflow-y: scroll;
    }

    .cds--table-expand[data-previous-value=collapsed] .cds--table-expand__svg, .dropShipment svg {
      stroke: #fff;
    }

    .orderStatus {
      color: #fff;

      .expirationDate {
        color: #fff;
      }

      span {
        border-color: #fff;
      }
    }

    .supplierStock {
      color: #fff;
    }

    & + tr[data-child-row] td {
      padding: 15px 0;
      border: 1px solid #B2C2D5;
      background: #fff;
    }

    td {
      background: #040F2A;
      color: #fff;
      fill: #fff;
    }
  }

  .cds--structured-list {
    margin: 0;

    .productInfo {
      display: flex;
      justify-content: space-around;

      span {
        padding: 10px;
      }
    }

    .productName {
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .productPrice {
      text-transform: capitalize;
    }

    .cds--structured-list-row {
      border: none;

      &:nth-of-type(odd) {
        background-color: #F4F7FC;
      }
    }

    .cds--structured-list-td {
      padding: 12px;
    }
  }

  @media screen and (max-width: 1570px) and (min-width: ${ props => props.theme.device.laptop }) {
    .cds--data-table-container {
      max-width: calc(100vw - 380px);
      overflow-x: auto;
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .pageTitle {
      margin-bottom: 20px;

      .pageTitleActions {
        > span {
          display: none;
        }
      }
    }

    .cds--data-table {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          position: relative;

          &:hover {
            background: #fff;
          }

          &:first-of-type {
            &:hover td {
              border: none;
            }

            td {
              border: none;
            }
          }

          &.cds--parent-row {
            display: flex;
            flex-direction: column;
            height: auto;
            margin-top: 20px;

            &.cds--expandable-row {
              .cds--table-expand {
                background: #040F2A;
              }

              td .mobileTableHeader span svg {
                fill: #fff;
              }
            }

            &.reservation.cds--expandable-row td {
              background: #040F2A;
            }

            td {
              border: none;

              &.cds--table-expand {
                width: auto;
                display: flex;
                justify-content: end;
                padding-right: 10px;

                .cds--table-expand__button {
                  width: 25px;
                  display: inline-block;
                }
              }

              &:first-of-type {
                height: 60px;
                background: #F5F7F9;
              }

              .orderNumber {
                > span {
                  display: none;
                }

                .mobileTableHeader {
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  height: 40px;
                  position: absolute;
                  top: 10px;
                  left: 10px;
                  right: 50px;

                  .left {
                    display: flex;
                    align-items: center;

                    .label {
                      display: flex;
                      align-items: center;
                      font-weight: 600;
                      min-width: 140px;

                      svg {
                        width: 20px;
                        margin-right: 10px;
                      }
                    }
                  }

                  button {
                    cursor: pointer;
                    border: none;
                    background: transparent;
                  }
                }
              }

              .actions {
                display: flex;
                align-items: center;
                justify-content: center;
                gap: 10px;
                padding: 10px 0;

                button {
                  flex: 1;

                  span {
                    display: inline-block;
                    margin-left: 10px;
                  }
                }

                a {
                  flex: 1;

                  span {
                    display: inline-block;
                    margin-left: 10px;
                    color: #3A3A3A;
                  }
                }

                .downloads {
                  display: none;
                }
              }
            }
          }
        }
      }
    }

    .cds--structured-list {

      .cds--structured-list-row {
        display: flex;
        flex-direction: column;
        align-items: center;

        .cds--structured-list-td {
          padding: 6px;
          width: 100%;
          max-width: calc(100vw - 70px);
        }

        .productInfo {
          display: flex;
          justify-content: space-between;
          align-items: center;

          span {
            padding: 0;
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
  @media screen and (max-width: ${ props => props.theme.device.laptop }) {

    &.cds--table-expand {
      align-self: end;
    }

    &[data-label="dropShipment"] {
      display: none;
    }

    &[data-label="invoiceNumber"],
    &[data-label="added"],
    &[data-label="userFullName"],
    &[data-label="status"],
    &[data-label="orderType"],
    &[data-label="totalValue"] {    
      display: flex;
      align-items: center;
      height: 40px;
      padding-left: 150px;

      &:before {
        content: "${ props => props.name }:";
        position: absolute;
        left: 10px;
        width: 170px;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
      }
    }
  } 
`;

const StyledStatus = styled.span`
  color: ${ props => statusColors[props.statusId] || 'inherit' };
  font-weight: 600;
  border: ${ props => props.statusId === 2 ? '1px solid #813d94' : 'none' };
  border-radius: 6px;
  padding: 0 5px;
`;

const OrdersComponent = ({
  orders,
  ordersLoading,
  pagination,
  onDeleteReservation,
  deleteReservationIdLoading,
  onDownload,
  ordersById,
  onGetOrderById,
  sortOptions,
  defaultSortId,
  currentSort,
  handleMobileOrderChange,
  errors,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate, formatNumber } = useNumberUtils();

  const headerData = [
    {
      key: 'dropShipment',
      header: '',
    },
    {
      key: 'orderNumber',
      header: t('pages.id'),
    },
    {
      key: 'invoiceNumber',
      header: t('pages.invoiceNumber'),
    },
    {
      key: 'added',
      header: t('pages.added'),
    },
    {
      key: 'userFullName',
      header: t('pages.user'),
    },
    {
      key: 'status',
      header: t('pages.orderStatus'),
    },
    {
      key: 'orderType',
      header: t('pages.orderType'),
    },
    {
      key: 'deliveryStock',
      header: t('pages.deliveryStock'),
    },
    {
      key: 'totalValue',
      header: t('pages.totalValue'),
    },
    {
      key: 'actions',
      header: t('pages.actions'),
    },
  ];

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'orderNumber') {
      mappedLabel = 'id';
    }
    if (label === 'userFullName') {
      mappedLabel = 'user';
    }
    if (label === 'status') {
      mappedLabel = 'orderStatus';
    }
    return t(`pages.${mappedLabel}`);
  };

  const renderTableHeader = header => {
    const mappedSortingParams = {
      orderNumber: 'order_number',
      invoiceNumber: 'invoice_number',
      added: 'added',
      userFullName: 'user',
      status: 'internal_status',
      orderType: 'order_type',
      deliveryStock: 'delivery_stock',
      totalValue: 'total_value',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (header.key === 'dropShipment' || header.key === 'actions') ? <span>{ header.header }</span> : (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const renderCell = cell => {
    if (cell.info.header === 'dropShipment') {
      const uid = uniqueId();

      return cell.value !== 0 && (
        <div className="dropShipment">
          <span id={ `ds-tooltip-${ uid }}` }><Truck /></span>
          <ReactTooltip
            className="dsTooltip"
            anchorId={ `ds-tooltip-${ uid }}` }
            variant="light"
            offset={ 10 }
            content={ (
              <p>{ t('pages.dropShipmentOrder') }</p>
            ) }
          />
        </div>
      );
    }

    if (cell.info.header === 'orderNumber') {
      const { id, invoiceId, statusId, dropShipment } = cell.value;
      return (
        <div className="orderNumber">
          <div className="mobileTableHeader">
            <div className="left">
              <span className="label">
                { dropShipment !== 0 && <Truck /> }
                <span>{ `${ t('orders.idOrder') }:`}</span>
              </span>
              <span>{` ${ id }` }</span>
            </div>
            { (invoiceId && statusId !== 2) && (
              <div>
                <button
                  type="button"
                  className="download"
                  onClick={ () => onDownload('invoice', false, invoiceId) }
                >
                  <IconWarranty />
                </button>
                <button
                  type="button"
                  className="download"
                  onClick={ () => onDownload('invoice', true, invoiceId) }
                >
                  <IconInvoice />
                </button>
              </div>
            ) }
          </div>
          <span>{ id }</span>
        </div>
      );
    }

    if (cell.info.header === 'added') {
      return formatDate(cell.value, { timeStyle: undefined });
    }

    if (cell.info.header === 'status') {
      const { status, statusName, statusId, expirationDate } = cell.value;
      return (
        <div className="orderStatus">
          <StyledStatus
            className="orderStatus"
            status={ status }
            statusId={ statusId }
          >
            { statusName }
          </StyledStatus>
          { (statusId === 2 && expirationDate) && <span className="expirationDate">{ `${ t('orders.expirationDate') } ${ expirationDate }` }</span> }
        </div>
      );
    }

    if (cell.info.header === 'deliveryStock') {
      const deliveryStock = cell.value;
      return <span className={ deliveryStock === 'supplier' ? 'supplierStock' : '' }>{ deliveryStock === 'supplier' ? t('products.supplierStock') : t('products.nodStock') }</span>;
    }

    if (cell.info.header === 'totalValue') {
      const uid = uniqueId();
      const {
        isCashOnDelivery,
        totalValue,
        totalCashOnDelivery,
        totalWitchVat,
        dropShipment,
        dropShipmentCurrency
      } = cell.value;

      let tooltipContent;

      if (dropShipment) {
        tooltipContent = (
          <p>{ t('pages.totalWithVatForClient', { totalCashOnDelivery: formatNumber(totalCashOnDelivery, { currency: dropShipmentCurrency }) }) }</p>
        );
      } else {
        tooltipContent = (
          <p>{ t('pages.totalWithVat', { totalCashOnDelivery: formatNumber(totalWitchVat, { currency: CURRENCY_RON }) }) }</p>
        );
      }

      return (
        <div className="totalValue">
          { isCashOnDelivery && (
            <div className="cod">
              <span id={ `cod-tooltip-${ uid }}` }><MapPinCheckInside /></span>
              <ReactTooltip
                className="codTooltip"
                anchorId={ `cod-tooltip-${ uid }}` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('pages.cashOnDelivery') }</p>
                ) }
              />
            </div>
          ) }
          <span>{ formatNumber(totalValue, { currency: CURRENCY_RON }) }</span>
          <div className="tcd">
            <span id={ `tcd-tooltip-${ uid }}` }><Info size={ 18 } /></span>
            <ReactTooltip
              className="tcdTooltip"
              anchorId={ `tcd-tooltip-${ uid }}` }
              variant="light"
              offset={ 10 }
              content={ tooltipContent }
            />
          </div>
        </div>
      );
    }

    if (cell.info.header === 'actions') {
      const { orderId, statusId, invoiceId, id } = cell.value;
      const uid = uniqueId();

      return (
        <div className="actions">
          { (statusId === 2) ? (
            <>
              <AnalyticsLink
                id={ `editOrder-${ uid }` }
                className={ `editOrder ${ deleteReservationIdLoading === orderId ? 'disableLink' : '' }` }
                to={ `/orders/${ orderId }` }
                event={ { action: 'reservation_edit_clicked', params: { order_number: id } } }
              >
                <Pencil size={ 18 } />
                <span>{ t('orders.edit') }</span>
              </AnalyticsLink>
              <ReactTooltip
                className="editOrder"
                anchorId={ `editOrder-${ uid }` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('common.edit') }</p>
                ) }
              />
              <button
                id={ `deleteOrder-${ uid }` }
                className="deleteOrder"
                type="button"
                onClick={ () => onDeleteReservation(orderId) }
                disabled={ deleteReservationIdLoading === orderId }
              >
                { deleteReservationIdLoading === orderId
                  ? <Loading active small withOverlay={ false } />
                  : <><Trash size={ 18 } /><span>{ t('orders.cancel') }</span></>
                }
              </button>
              <ReactTooltip
                className="deleteOrder"
                anchorId={ `deleteOrder-${ uid }` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('common.delete') }</p>
                ) }
              />
            </>
          ) : (
            <>
              { invoiceId && (
                <div className="downloads">
                  <button
                    type="button"
                    className="download"
                    onClick={ () => onDownload('invoice', false, invoiceId) }
                  >
                    <IconWarranty />
                  </button>
                  <button
                    type="button"
                    className="download"
                    onClick={ () => onDownload('invoice', true, invoiceId) }
                  >
                    <IconInvoice />
                  </button>
                </div>
              ) }
              <AnalyticsLink
                id={ `viewOrder-${ uid }` }
                className="viewOrder"
                to={ `/orders/${ orderId }` }
                event={ { action: 'order_view', params: { type: id } } }
              >
                <Eye size={ 20 } />
                <span>{ t('orders.showInfo') }</span>
              </AnalyticsLink>
              <ReactTooltip
                className="viewOrder"
                anchorId={ `viewOrder-${ uid }` }
                variant="light"
                offset={ 10 }
                content={ (
                  <p>{ t('common.view') }</p>
                ) }
              />
            </>
          ) }
        </div>
      );
    }

    return cell.value;
  };

  return (
    <StyledOrders theme={ theme }>
      <div className="pageTitle">
        <h3>{ t('orders.orders') }</h3>
        <div className="pageTitleActions">
          <span>{ t('orders.downloadOrders') }:</span>
          <button
            type="button"
            className="download"
            onClick={ () => onDownload('feed', true) }
          >
            <IconInvoice />
          </button>
          <button
            type="button"
            className="download"
            onClick={ () => onDownload('feed', false) }
          >
            <IconCSV />
          </button>
        </div>
      </div>
      <NWNotification errors={ errors } delay={ 10 } />
      <FilterOrders
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
      />
      { ordersLoading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ orders } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps, getRowProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    <TableExpandHeader />
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { orders.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  {rows.map(row => (
                    <React.Fragment key={ row.id }>
                      <TableExpandRow
                        className={ row.cells[8].value.statusId === 2 ? 'reservation' : '' }
                        { ...getRowProps({ row, onClick: () => onGetOrderById(row.id) }) }
                      >
                        { row.cells.map(cell => (
                          <StyledTableCell
                            data-label={ cell.info.header }
                            name={ () => mapHeaderToLabel(cell.info.header) }
                            key={ cell.id }
                          >
                            { renderCell(cell) }
                          </StyledTableCell>
                        ))}
                      </TableExpandRow>
                      <TableExpandedRow colSpan={ headers.length + 1 }>
                        { !ordersById?.[row.id] ? <StructuredListSkeleton rowCount={ 2 } /> : (
                          <StructuredListWrapper isFlush={ false }>
                            <StructuredListBody>
                              { ordersById?.[row.id].map(product => (
                                <StructuredListRow>
                                  <StructuredListCell className="productName" noWrap>{ product?.name }</StructuredListCell>
                                  <StructuredListCell>
                                    <div className="productInfo">
                                      <span>{ `${product?.quantity } ${ t('orders.products') }`}</span>
                                      <span className="productPrice">{ formatNumber(product?.totalPromoPrice, { currency: CURRENCY_RON }) }</span>
                                    </div>
                                  </StructuredListCell>
                                </StructuredListRow>
                              ))}
                            </StructuredListBody>
                          </StructuredListWrapper>
                        ) }
                      </TableExpandedRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <OrdersPagination loading={ ordersLoading } { ...pagination } />
    </StyledOrders>
  );
};

OrdersComponent.propTypes = {
  orders: arrayOf(shape({
    id: oneOfType([number, string]),
    awb: string,
    status: string,
    actions: string,
  })),
  ordersLoading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  onDeleteReservation: func,
  deleteReservationIdLoading: string,
  onDownload: func,
  ordersById: object,
  onGetOrderById: func,
  sortOptions: array,
  defaultSortId: string,
  currentSort: object,
  handleMobileOrderChange: func,
  errors: array,
};

OrdersComponent.displayName = 'OrdersComponent';

export default OrdersComponent;
